/**
 * «HVILKE ADVANCER MANGLER NOE?» (`GET /v1/editions/:id/advance-gaps`, AI-ANALYSE §6
 * `advance_check`).
 *
 * Regnet i basen fra de samme radene advance-fanen, reisefølget, reisene, overnattingen og kontrakten
 * viser — ingen egen «status» som kan bli uenig med dem:
 *
 *   rider          ingen fil og ingen lenke merket rider i programpostens advance-seksjoner
 *   technical      Teknisk-seksjonen har ingen leveranser, eller minst én som ingen har bestilt
 *   hospitality    det samme for Hospitality (`pending`: ikke bestilt — `summariseDeliveries`)
 *   party          ingen i reisefølget
 *   arrival        ingen reise av typen ankomst
 *   departure      ingen reise av typen avreise
 *   accommodation  ingen overnatting
 *   contract       ingen gjeldende kontrakt, og programposten står ikke som signert. Bare for den som
 *                  kan se kontrakter (`contract:read`); ellers regnes den aldri
 *
 * Programpostene er årgangens, snevret som programmet (`performance:list`). Avlyste står ikke med.
 * `missing` filtrerer på minst én av verdiene, og svaret sier hvilke av DEM som mangler.
 */
import { z } from 'zod';

import { performanceStatusSchema } from '../enums.js';
import { MAX_CURSOR_LENGTH, page } from '../http/pagination.js';

export const ADVANCE_GAP_KINDS = [
  'rider',
  'hospitality',
  'technical',
  'party',
  'arrival',
  'departure',
  'accommodation',
  'contract',
] as const;
export const advanceGapKindSchema = z.enum(ADVANCE_GAP_KINDS).meta({ id: 'AdvanceGapKind' });
export type AdvanceGapKind = z.infer<typeof advanceGapKindSchema>;

const splitKinds = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',').map((part) => part.trim());
};

export const listAdvanceGapsQuerySchema = z
  .object({
    /** Kommaliste. Utelatt: alle åtte. */
    missing: z
      .preprocess(
        splitKinds,
        z
          .array(advanceGapKindSchema)
          .min(1)
          .max(ADVANCE_GAP_KINDS.length)
          .transform((kinds) => [...new Set(kinds)]),
      )
      .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
  })
  .meta({ id: 'ListAdvanceGapsQuery' });
export type ListAdvanceGapsQuery = z.infer<typeof listAdvanceGapsQuerySchema>;

export const advanceGapDtoSchema = z
  .object({
    performanceId: z.uuid(),
    /** Programpostens egen tittel, ellers artistens navn. Null når ingen av delene finnes. */
    title: z.string().nullable(),
    artistName: z.string().nullable(),
    stageName: z.string().nullable(),
    startsAt: z.iso.datetime().nullable(),
    status: performanceStatusSchema,
    /** Det som mangler, blant det som ble spurt om, i rekkefølgen over. Aldri tom. */
    missing: z.array(advanceGapKindSchema).min(1),
  })
  .meta({ id: 'AdvanceGap' });
export type AdvanceGapDto = z.infer<typeof advanceGapDtoSchema>;

export const advanceGapListResponseSchema = page(advanceGapDtoSchema, 'AdvanceGapListResponse');
export type AdvanceGapListResponse = z.infer<typeof advanceGapListResponseSchema>;
